import React, { memo, useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  ChartContainer,
  TradingViewContainer,
  ChartInfo,
} from "./GridChartStyles";
import { useGetPatternData } from "./hooks/useGetPatternData";
import { useGetFilterBars } from "./hooks/useGetFilterBars";
import { useSetupChart } from "./hooks/useSetupChart";
import { useDimensions } from "./hooks/useDimensions";
import PatternLines from "./PatternLines/PatternLines";
import { removeDPattern } from "../../../../redux/slices/detectedPatterns";

import { addLayoutChart } from "../../../../redux/slices/chartLayout";
import { setChartDisplay } from "../../../../redux/slices/display";
import { navigate } from "../../../../redux/slices/navigation";

export default memo(function GridChart({
  type,
  pattern,
  height,
  width,
  toolbar,
}) {
  const showLines = useSelector(({ grid }) => grid.showLines);

  const { patternRange, patterns } = useGetPatternData(pattern.shapes);

  const filteredBars = useGetFilterBars(patternRange, pattern.chartId);

  const dimensions = useDimensions(filteredBars, height, width);

  useSetupChart({ filteredBars, id: type + pattern.id, height, width });

  // Redux dispatch function
  const dispatch = useDispatch();

  // Remove chart from detected patterns if no bars available
  useEffect(() => {
    if (!filteredBars || !filteredBars.length) {
      dispatch(removeDPattern(pattern.id));
    }
  });

  const onLinkClick = useCallback(
    (e) => {
      const range = {
        start: filteredBars[0].time * 1000,
        end: filteredBars[filteredBars.length - 1].time * 1000,
      };
      e && e.stopPropagation && e.stopPropagation();
      if (!dispatch(addLayoutChart(pattern.chartId))) return;
      dispatch(setChartDisplay());
      dispatch(navigate(pattern.chartId, range));
    },
    [dispatch, pattern.chartId, filteredBars]
  );

  // useState hooks
  const [transitioning, setTransitioning] = useState(false);

  // Handler functions
  const onGridChartLeave = () => {
    setTransitioning(true);
    setTimeout(() => setTransitioning(false), 200);
  };

  const intervalName =
    pattern.interval === 15
      ? "15m"
      : pattern.interval === 60
      ? "1h"
      : pattern.interval === 240
      ? "4h"
      : pattern.interval === 1440
      ? "1D"
      : pattern.interval;

  return (
    <ChartContainer
      height={height}
      width={width}
      toolbar={toolbar}
      transitioning={transitioning}
      onClick={() => !toolbar && onLinkClick()}
      onMouseLeave={onGridChartLeave}
    >
      <ChartInfo>
        <p>
          {pattern.chartName} {intervalName}
        </p>
      </ChartInfo>

      <TradingViewContainer id={type + pattern.id} />

      {showLines && (
        <PatternLines
          filteredBars={filteredBars}
          patterns={patterns}
          dimensions={dimensions}
        />
      )}
    </ChartContainer>
  );
});
